import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { copyQuestionSet, getQuestionSet } from "@/api/question-sets";
import { startRun } from "@/api/runs";
import { getLearnerStats, getMistakes } from "@/api/learner";
import type { QuestionSetItemRead } from "@/api/library-types";
import type { LearnerTopicStat, MistakeTopic } from "@/api/stats-types";
import type { TFunction } from "i18next";
import { buildPlaylist, CARD_KINDS } from "@/practice/playlist";
import { progressStore } from "@/practice/progress-store";
import { useProgressHydrated } from "@/practice/use-progress";
import { formatRelativeDate } from "@/train/set-card";
import { MathText } from "@/components/math-text";
import { Alert, Button, Spinner } from "@/components/ui";
import { CheatsheetCards } from "./cheatsheet-cards";

export type SetStatus = "done" | "in_progress" | "not_started";

/** Three-state done/not-done badge, shared by the Library list + set detail. */
export function SetStatusBadge({ status }: { status: SetStatus }) {
  const { t } = useTranslation();
  const cfg = {
    done: { label: t("Done"), cls: "bg-emerald-100 text-emerald-700" },
    in_progress: { label: t("In progress"), cls: "bg-amber-100 text-amber-700" },
    not_started: { label: t("Not started"), cls: "bg-black/5 text-ink-faint" },
  }[status];
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cfg.cls}`}
    >
      {status === "done" ? "✓ " : ""}
      {cfg.label}
    </span>
  );
}

function pick(c: Record<string, unknown>, k: string): string {
  return typeof c[k] === "string" ? (c[k] as string) : "";
}

function itemPrompt(it: QuestionSetItemRead): string {
  const c = it.content;
  return (
    pick(c, "question") ||
    pick(c, "statement") ||
    pick(c, "template") ||
    pick(c, "passage") ||
    pick(c, "prompt") ||
    pick(c, "front") ||
    pick(c, "instruction") ||
    pick(c, "relationship_prompt") ||
    pick(c, "scenario") ||
    it.question_type
  );
}

function itemAnswer(it: QuestionSetItemRead, t: TFunction): string {
  const c = it.content as Record<string, unknown>;
  const opts = c.options as { id: string; text: string }[] | undefined;
  if (Array.isArray(opts) && typeof c.correct_option_id === "string") {
    const o = opts.find((x) => x.id === c.correct_option_id);
    if (o?.text) return o.text;
  }
  if (typeof c.back === "string") return c.back;
  if (typeof c.model_answer === "string") return c.model_answer;
  if (typeof c.correct === "boolean") return c.correct ? t("True") : t("False");
  const blanks = c.blanks as { correct: string }[] | undefined;
  if (Array.isArray(blanks)) return blanks.map((b) => b.correct).join(", ");
  return "";
}

/** Compact per-topic mastery chips, joined from /learner/stats + /learner/mistakes
 *  on the bulk's topic_slug. Renders nothing when the learner has no history for
 *  the topic — these stats are global per-topic (not per-set). */
export function TopicStatChips({
  stat,
  mistake,
}: {
  stat?: LearnerTopicStat;
  mistake?: MistakeTopic;
}) {
  const { t } = useTranslation();
  const chips: { label: string; tone: string }[] = [];
  if (stat && stat.n_attempts > 0) {
    if (stat.mastery != null)
      chips.push({
        label: t("Mastery {{pct}}%", { pct: Math.round(stat.mastery * 100) }),
        tone: "bg-brand-100 text-brand-700",
      });
    if (stat.percentile != null)
      chips.push({ label: t("Top {{pct}}%", { pct: stat.percentile }), tone: "bg-purple-100 text-purple-700" });
    if (stat.recent_accuracy != null)
      chips.push({
        label: t("{{pct}}% recent", { pct: Math.round(stat.recent_accuracy * 100) }),
        tone: "bg-black/5 text-ink-soft",
      });
  }
  if (mistake && mistake.miss_count > 0)
    chips.push({
      label: t("{{count}} missed", { count: mistake.miss_count }),
      tone: "bg-red-100 text-red-700",
    });
  if (chips.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {chips.map((c) => (
        <span key={c.label} className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${c.tone}`}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

export function SetDetailPage() {
  const { t } = useTranslation();
  const { setId = "" } = useParams();
  const hydrated = useProgressHydrated();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["question-set", setId],
    queryFn: () => getQuestionSet(setId),
    enabled: !!setId,
  });

  // Per-topic mastery / mistakes (global per-topic; joined to bulks by slug).
  const statsQuery = useQuery({ queryKey: ["learner-stats"], queryFn: getLearnerStats });
  const mistakesQuery = useQuery({ queryKey: ["learner-mistakes"], queryFn: getMistakes });
  const topicStatBySlug = useMemo(
    () => new Map((statsQuery.data?.topics ?? []).map((t) => [t.topic_slug, t])),
    [statsQuery.data],
  );
  const mistakeBySlug = useMemo(
    () => new Map((mistakesQuery.data?.topics ?? []).map((t) => [t.topic_slug, t])),
    [mistakesQuery.data],
  );

  // Answer reveal: hidden by default (tap-to-reveal), with a set-level switch.
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [revealAll, setRevealAll] = useState(false);
  const isRevealed = (id: string) => revealAll || revealedIds.has(id);
  const toggleReveal = (id: string) =>
    setRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const playableCount = useMemo(() => (data ? buildPlaylist(data).length : 0), [data]);
  const byId = useMemo(
    () => new Map((data?.items ?? []).map((it) => [it.id, it])),
    [data],
  );

  // Per-set progress from the shared /learner/progress blob (same source as iOS).
  const stats = progressStore.lifetimeStats(setId);
  const sessionsCompleted = progressStore.sessionsCompleted(setId);
  const completedBulks = progressStore.completedBulkIds(setId);
  const cursor = progressStore.cursor(setId);
  const lastAt = progressStore.lastActivityAt(setId);
  const wrongCount = hydrated ? progressStore.wrongIds(setId).size : 0;
  const hasProgress = hydrated && (stats.attemptCount > 0 || sessionsCompleted > 0);
  const allBulksDone =
    !!data && data.bulks.length > 0 && completedBulks.size >= data.bulks.length;
  const canResume = hydrated && !!cursor && !allBulksDone;
  const [synthBusy, setSynthBusy] = useState(false);
  const [synthMsg, setSynthMsg] = useState<string | null>(null);
  const synthesize = async () => {
    setSynthBusy(true);
    setSynthMsg(null);
    try {
      await startRun("synthesize-sets", { source_set_ids: [setId] });
      setSynthMsg(t("Synthesizing… your diverse new set will appear in your Library shortly."));
    } catch {
      setSynthMsg(t("Couldn't start synthesis. Please try again."));
    } finally {
      setSynthBusy(false);
    }
  };

  const [followBusy, setFollowBusy] = useState(false);
  const [followMsg, setFollowMsg] = useState<string | null>(null);
  const buildFollowUp = async () => {
    setFollowBusy(true);
    setFollowMsg(null);
    try {
      await startRun("learn-a-topic", { reference_set_id: setId, rigour: "deep" });
      setFollowMsg(t("Building a follow-up… it'll appear in your Library shortly."));
    } catch {
      setFollowMsg(t("Couldn't start the follow-up. Please try again."));
    } finally {
      setFollowBusy(false);
    }
  };

  const [copyBusy, setCopyBusy] = useState(false);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  const copyToLibrary = async () => {
    setCopyBusy(true);
    setCopyMsg(null);
    try {
      const copy = await copyQuestionSet(setId);
      setCopyMsg(t("Copied to your library as “{{title}}”.", { title: copy.title }));
    } catch {
      setCopyMsg(t("Couldn't copy this set. Please try again."));
    } finally {
      setCopyBusy(false);
    }
  };

  const started = hydrated && progressStore.hasStarted(setId);
  const setStatus: "done" | "in_progress" | "not_started" = !hydrated
    ? "not_started"
    : allBulksDone
      ? "done"
      : started
        ? "in_progress"
        : "not_started";

  return (
    <div className="mx-auto max-w-2xl animate-fade-up lg:max-w-4xl">
      <Link to="/app/library" className="text-sm text-brand-600 hover:underline">
        ← {t("Library")}
      </Link>

      {isLoading && (
        <div className="flex justify-center py-20 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {isError && (
        <div className="mt-6">
          <Alert>{error instanceof Error ? error.message : t("Set not found.")}</Alert>
        </div>
      )}

      {data && (
        <>
          <div className="mt-4 flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{data.title}</h1>
            {hydrated && <SetStatusBadge status={setStatus} />}
          </div>
          {data.description && <p className="mt-1 text-ink-faint">{data.description}</p>}

          {data.project_id && (
            <Link
              to={`/app/projects/${data.project_id}`}
              className="mt-2 inline-flex w-fit items-center gap-1 rounded-full bg-violet-50 px-3 py-1 text-sm font-medium text-violet-700 transition hover:bg-violet-100"
            >
              📂 {data.project_title
                ? t("From project: {{title}}", { title: data.project_title })
                : t("From a project")}
              <span aria-hidden>→</span>
            </Link>
          )}

          {hasProgress && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-full bg-black/5 px-2.5 py-1 text-ink-soft">
                {t("Lifetime: {{correct}}/{{total}} correct", {
                  correct: stats.correctCount,
                  total: stats.attemptCount,
                })}
                {stats.accuracyPercent !== null && ` · ${stats.accuracyPercent}%`}
              </span>
              {wrongCount > 0 && (
                <span className="rounded-full bg-red-100 px-2.5 py-1 font-medium text-red-700">
                  {t("{{count}} to review", { count: wrongCount })}
                </span>
              )}
              {sessionsCompleted > 0 && (
                <span className="rounded-full bg-purple-100 px-2.5 py-1 font-medium text-purple-700">
                  {sessionsCompleted === 1
                    ? t("✦ Mastered {{count}} time", { count: sessionsCompleted })
                    : t("✦ Mastered {{count}} times", { count: sessionsCompleted })}
                </span>
              )}
              {lastAt && (
                <span className="text-xs text-ink-faint">{t("Practised {{when}}", { when: formatRelativeDate(lastAt, t) })}</span>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {canResume ? (
              <>
                <Link to={`/app/library/${data.id}/play?resume=1`}>
                  <Button size="lg" disabled={playableCount === 0}>
                    {t("Continue")}
                  </Button>
                </Link>
                <Link to={`/app/library/${data.id}/play`}>
                  <Button size="lg" variant="secondary" disabled={playableCount === 0}>
                    {t("From start")}
                  </Button>
                </Link>
              </>
            ) : (
              <Link to={`/app/library/${data.id}/play`}>
                <Button size="lg" disabled={playableCount === 0}>
                  {playableCount > 0 ? t("Practise ({{count}})", { count: playableCount }) : t("Practise")}
                </Button>
              </Link>
            )}
            {playableCount === 0 && (
              <span className="text-sm text-ink-faint">{t("No practisable questions in this set.")}</span>
            )}
            <Button size="lg" variant="secondary" disabled={synthBusy} onClick={synthesize}>
              {synthBusy ? t("Starting…") : t("✦ Synthesize diverse set")}
            </Button>
            <Button size="lg" variant="secondary" disabled={followBusy} onClick={buildFollowUp}>
              {followBusy ? t("Starting…") : t("✦ Build a follow-up")}
            </Button>
            <Button size="lg" variant="secondary" disabled={copyBusy} onClick={copyToLibrary}>
              {copyBusy ? t("Copying…") : t("Copy to my library")}
            </Button>
          </div>
          {synthMsg && <p className="mt-2 text-sm text-ink-soft">{synthMsg}</p>}
          {followMsg && <p className="mt-2 text-sm text-ink-soft">{followMsg}</p>}
          {copyMsg && <p className="mt-2 text-sm text-ink-soft">{copyMsg}</p>}

          {data.source_pdf_url && (
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">
                  {t("Source PDF")}
                </h2>
                <a
                  href={data.source_pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="text-sm text-brand-600 hover:underline"
                >
                  {t("Download ↓")}
                </a>
              </div>
              <iframe
                title={t("Source PDF")}
                src={data.source_pdf_url}
                className="mt-3 h-[70vh] w-full rounded-lg border border-black/10"
              />
            </div>
          )}

          <CheatsheetCards
            data={data}
            topicStatBySlug={topicStatBySlug}
            mistakeBySlug={mistakeBySlug}
          />

          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">{t("Topics")}</h2>
              <button
                onClick={() => setRevealAll((v) => !v)}
                className="text-xs font-medium text-brand-600 hover:underline"
              >
                {revealAll ? t("Hide answers") : t("Reveal answers")}
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {[...data.bulks]
                .sort((a, b) => a.order - b.order)
                .map((b) => {
                  const items = b.sequence
                    .map((id) => byId.get(id))
                    .filter((it): it is QuestionSetItemRead => !!it);
                  const gradedIds = items
                    .filter((it) => !CARD_KINDS.has(it.item_kind))
                    .map((it) => it.id);
                  const counts = progressStore.bulkOutcomeCounts(setId, gradedIds);
                  return (
                    <details key={b.id} className="card p-3.5">
                      <summary className="flex cursor-pointer items-center justify-between">
                        <div className="flex items-center gap-2">
                          {hydrated && completedBulks.has(b.id) && (
                            <span
                              className="text-emerald-600"
                              title={t("Completed")}
                              aria-label={t("Completed")}
                            >
                              ✓
                            </span>
                          )}
                          <div>
                            <p className="font-medium text-ink">{b.topic_label}</p>
                            <p className="text-xs capitalize text-ink-faint">{b.bulk_kind}</p>
                          </div>
                        </div>
                        <span className="text-sm text-ink-faint">
                          {hasProgress && gradedIds.length > 0
                            ? t("{{correct}}/{{total}} correct", {
                                correct: counts.correct,
                                total: counts.total,
                              })
                            : b.sequence.length === 1
                              ? t("{{count}} item", { count: b.sequence.length })
                              : t("{{count}} items", { count: b.sequence.length })}
                        </span>
                      </summary>

                      {hasProgress && gradedIds.length > 0 && (
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-black/5">
                          <div
                            className="h-full rounded-full bg-brand-500"
                            style={{
                              width: `${Math.round((counts.correct / counts.total) * 100)}%`,
                            }}
                          />
                        </div>
                      )}

                      <TopicStatChips
                        stat={topicStatBySlug.get(b.topic_slug)}
                        mistake={mistakeBySlug.get(b.topic_slug)}
                      />

                      <ol className="mt-3 space-y-3 border-t border-black/5 pt-3">
                        {items.map((it, i) => {
                          const ans = itemAnswer(it, t);
                          const graded = !CARD_KINDS.has(it.item_kind);
                          const outcome = hydrated && graded ? progressStore.outcomeOf(setId, it.id) : null;
                          // Answers are locked until the learner has actually
                          // attempted the question — results shouldn't be visible
                          // for not-yet-done questions. Cards (no outcome) always
                          // show their content.
                          const attempted = !graded || (hydrated && progressStore.isAttempted(setId, it.id));
                          return (
                            <li key={it.id} className="text-sm">
                              <div className="flex gap-2">
                                <span
                                  className={
                                    outcome === "correct"
                                      ? "shrink-0 font-semibold text-emerald-600"
                                      : outcome === "wrong"
                                        ? "shrink-0 font-semibold text-rose-600"
                                        : graded && hydrated
                                          ? "shrink-0 text-ink-faint/50"
                                          : "shrink-0 text-ink-faint"
                                  }
                                  title={
                                    outcome === "correct"
                                      ? t("Answered correctly")
                                      : outcome === "wrong"
                                        ? t("Answered incorrectly")
                                        : graded && hydrated
                                          ? t("Not attempted yet")
                                          : undefined
                                  }
                                >
                                  {outcome === "correct"
                                    ? "✓"
                                    : outcome === "wrong"
                                      ? "✗"
                                      : graded && hydrated
                                        ? "○"
                                        : `${i + 1}.`}
                                </span>
                                <div className="min-w-0">
                                  <div className="text-ink">
                                    <MathText text={itemPrompt(it)} />
                                  </div>
                                  {ans &&
                                    (!attempted ? (
                                      <div className="mt-0.5 text-xs text-ink-faint">
                                        {t("🔒 Answer unlocks after you attempt this")}
                                      </div>
                                    ) : isRevealed(it.id) ? (
                                      <div className="mt-0.5 text-xs text-emerald-700">
                                        <span className="font-semibold">{t("Answer:")} </span>
                                        <MathText text={ans} />
                                      </div>
                                    ) : (
                                      <div className="mt-0.5">
                                        <button
                                          onClick={() => toggleReveal(it.id)}
                                          className="text-xs font-medium text-brand-600 hover:underline"
                                        >
                                          {t("Show answer")}
                                        </button>
                                      </div>
                                    ))}
                                  <div className="mt-0.5">
                                    <span className="inline-block rounded bg-black/5 px-1.5 py-0.5 text-[10px] text-ink-faint">
                                      {it.question_type}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    </details>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
